
import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./VendorCare.css";
import * as ticketService from "../../../Servicefile/internalTicketService";

// ================= STATUS OPTIONS =================
const STATUS_OPTIONS = ["OPEN", "IN_PROGRESS", "RESOLVED", "REJECTED"];

// ================= TICKET TYPE OPTIONS =================
const TICKET_TYPE_OPTIONS = [
    "SYSTEM_BUG",
    "FEATURE_REQUEST",
    "DATA_DISCREPANCY",
    "ACCESS_ISSUE",
    "PERFORMANCE",
    "OTHER",
];

// ================= DEPARTMENT OPTIONS =================
const DEPARTMENT_OPTIONS = [
    "OPERATIONS",
    "FINANCE",
    "TECH",
    "CUSTOMER_SUPPORT",
    "MARKETING",
];

// ================= PRIORITY OPTIONS =================
const PRIORITY_OPTIONS = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
// ================= USER-FRIENDLY LABEL MAPPERS =================

// Map status to display labels
const getStatusLabel = (status) => {
    const labels = {
        "OPEN": "Open",
        "IN_PROGRESS": "In Progress",
        "RESOLVED": "Resolved",
        "REJECTED": "Rejected",
        "CLOSED": "Closed",
        "ESCALATED": "Escalated",
        "REOPENED": "Reopened",
    };
    return labels[status] || status;
};

// Map ticket type to display labels
const getTicketTypeLabel = (ticketType) => {
    const labels = {
        "SYSTEM_BUG": "System Bug",
        "FEATURE_REQUEST": "Feature Request",
        "DATA_DISCREPANCY": "Data Discrepancy",
        "ACCESS_ISSUE": "Access Issue",
        "PERFORMANCE": "Performance",
        "OTHER": "Other",
    };
    return labels[ticketType] || ticketType;
};

// Map department to display labels
const getDepartmentLabel = (department) => {
    const labels = {
        "OPERATIONS": "Operations",
        "FINANCE": "Finance",
        "TECH": "Tech",
        "CUSTOMER_SUPPORT": "Customer Support",
        "MARKETING": "Marketing",
        "HR": "HR",
    };
    return labels[department] || department;
};

// Map priority to display labels
const getPriorityLabel = (priority) => {
    const labels = {
        "LOW": "Low",
        "MEDIUM": "Medium",
        "HIGH": "High",
        "CRITICAL": "Critical",
    };
    return labels[priority] || priority;
};

// ================= BADGE CLASS FUNCTIONS =================

const getStatusBadgeClass = (status) => {
    switch (status) {
        case "OPEN": return "status-open";
        case "IN_PROGRESS": return "status-progress";
        case "RESOLVED": return "status-resolved";
        case "REJECTED": return "status-rejected";
        default: return "status-default";
    }
};

const getTicketTypeBadgeClass = (ticketType) => {
    switch (ticketType) {
        case "SYSTEM_BUG": return "type-food";
        case "FEATURE_REQUEST": return "type-service";
        case "DATA_DISCREPANCY": return "type-payment";
        case "ACCESS_ISSUE": return "type-delivery";
        case "PERFORMANCE": return "type-other";
        case "OTHER": return "type-default";
        default: return "type-default";
    }
};

const getPriorityBadgeClass = (priority) => {
    switch (priority) {
        case "HIGH": return "status-open";
        case "MEDIUM": return "status-progress";
        case "LOW": return "status-resolved";
        case "CRITICAL": return "status-open";
        default: return "status-default";
    }
};

const getAttachmentUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) {
        return url;
    }
    return "";
};

// ================= DATE & TIME CONVERSION (UTC -> IST 12-HOUR FORMAT) =================
const parseUtcDate = (dateStr) => {
    if (!dateStr) return null;
    let str = String(dateStr).trim();
    if (!str.endsWith("Z") && !str.includes("+") && !str.includes("-", 10)) {
        str += "Z";
    }
    const date = new Date(str);
    return isNaN(date.getTime()) ? null : date;
};

const formatToISTDate = (dateStr) => {
    const date = parseUtcDate(dateStr);
    if (!date) return "N/A";
    return date.toLocaleDateString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
    }).replace(/\//g, "-");
};

const formatToISTTime = (dateStr) => {
    const date = parseUtcDate(dateStr);
    if (!date) return "N/A";
    return date.toLocaleTimeString("en-IN", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
    });
};

const formatToISTDateTime = (dateStr) => {
    const date = parseUtcDate(dateStr);
    if (!date) return "N/A";
    return date.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
    });
};

const AttachmentViewer = ({ attachmentUrl, showImagePreview }) => {
    const [imgError, setImgError] = useState(false);

    useEffect(() => {
        setImgError(false);
    }, [attachmentUrl]);

    if (!attachmentUrl) return null;

    const fullUrl = getAttachmentUrl(attachmentUrl);

    if (!fullUrl) {
        return (
            <div className="attachment-section">
                <h6>Attachment</h6>
                <div style={{ padding: "12px", color: "#6c757d", fontStyle: "italic", backgroundColor: "#f8f9fa", borderRadius: "8px", border: "1px dashed #ccc" }}>
                    ⏳ Loading attachment...
                </div>
            </div>
        );
    }

    return (
        <div className="attachment-section">
            <h6>Attachment</h6>
            <div className="attachment-preview">
                {imgError ? (
                    <div className="attachment-error" style={{ color: "#dc3545", padding: "10px", textAlign: "center" }}>
                        ⚠️ Failed to load image
                    </div>
                ) : (
                    <img
                        src={fullUrl}
                        alt="Ticket attachment"
                        onError={() => setImgError(true)}
                        onClick={() => showImagePreview(fullUrl)}
                        style={{
                            maxWidth: "100%",
                            maxHeight: "300px",
                            objectFit: "contain",
                            borderRadius: "8px",
                            cursor: "pointer",
                            border: "1px solid #ddd",
                            padding: "5px",
                            backgroundColor: "#f8f9fa",
                        }}
                    />
                )}
                <div className="attachment-actions" style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
                    <a
                        href={fullUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="preview-btn preview-btn-primary"
                    >
                        🔍 Open Full Size
                    </a>
                    <a
                        href={fullUrl}
                        download
                        className="preview-btn preview-btn-secondary"
                    >
                        💾 Download
                    </a>
                </div>
            </div>
        </div>
    );
};

// ================= MAIN COMPONENT =================

const InternalTickets = () => {

    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedStatusFilter, setSelectedStatusFilter] = useState("");
    const [selectedTicketTypeFilter, setSelectedTicketTypeFilter] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [showViewModal, setShowViewModal] = useState(false);
    const [currentIssueForView, setCurrentIssueForView] = useState(null);
    const [newStatus, setNewStatus] = useState("");
    const [adminResponse, setAdminResponse] = useState("");
    const [imagePreviewModal, setImagePreviewModal] = useState({ show: false, url: "" });
    const [stats, setStats] = useState({ OPEN: 0, IN_PROGRESS: 0, RESOLVED: 0, REJECTED: 0, Total: 0 });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState({
        raisedBy: "",
        subject: "",
        description: "",
        department: "",
        priority: "",
        issueType: "",
        attachment: null,
    });
    const [attachmentPreview, setAttachmentPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [creating, setCreating] = useState(false);

    // Check if token exists
    const hasToken = useCallback(() => {
        const token = localStorage.getItem("authToken");
        if (!token) {
            setError("Authentication token not found. Please login again.");
            setLoading(false);
            return false;
        }
        return true;
    }, []);

    // Fetch all tickets
    const fetchTickets = useCallback(async () => {
        if (!hasToken()) return;

        setLoading(true);
        setError(null);
        try {
            const data = await ticketService.getAllTickets();
            const transformed = ticketService.transformTickets(data);
            setTickets(transformed);

            // Also fetch summary for stats
            await fetchSummary();
        } catch (err) {
            console.error('Failed to fetch tickets:', err);
            if (err.message.includes('403')) {
                setError('Access denied. Please check your permissions or login again.');
            } else {
                setError(`Failed to load tickets: ${err.message}`);
            }
            setTickets([]);
        } finally {
            setLoading(false);
        }
    }, [hasToken]);

    // Fetch summary
    const fetchSummary = useCallback(async () => {
        if (!hasToken()) return;

        try {
            const summary = await ticketService.getTicketSummary();
            setStats({
                OPEN: summary.open || 0,
                IN_PROGRESS: summary.inProgress || 0,
                RESOLVED: summary.resolved || 0,
                REJECTED: summary.rejected || 0,
                Total: summary.total || 0,
            });
        } catch (err) {
            console.error('Failed to fetch summary:', err);
        }
    }, [hasToken]);

    // Filter tickets based on status, type, and search
    const filteredTickets = useMemo(() => {
        return tickets.filter(ticket => {
            const matchesStatus = !selectedStatusFilter || ticket.status === selectedStatusFilter;
            const matchesType = !selectedTicketTypeFilter || ticket.issueType === selectedTicketTypeFilter;
            const matchesSearch = !searchTerm ||
                ticket.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                ticket.ticketId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                ticket.raisedBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                getDepartmentLabel(ticket.department)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                getTicketTypeLabel(ticket.issueType)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                ticket.subject?.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesStatus && matchesType && matchesSearch;
        }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }, [tickets, selectedStatusFilter, selectedTicketTypeFilter, searchTerm]);

    // Load tickets on mount
    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    // Handle viewing a ticket
    const handleShowViewModal = async (ticket) => {
        setCurrentIssueForView(ticket); // show list data immediately, no flash of empty modal
        setShowViewModal(true);
        setAdminResponse("");
        setNewStatus("");

        try {
            const full = await ticketService.getTicketById(ticket.ticketId);
            setCurrentIssueForView(ticketService.transformTicket(full));
        } catch (err) {
            console.error('Failed to fetch full ticket details:', err);
            // modal still shows list data, just without a working image
        }
    };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setCurrentIssueForView(null);
        setAdminResponse("");
        setNewStatus("");
    };

    // Handle saving changes (status and admin response)
    const handleSaveChanges = async () => {
        if (!currentIssueForView) return;
        if (!hasToken()) return;

        setSaving(true);
        try {
            await ticketService.updateTicket(
                currentIssueForView.ticketId,
                newStatus || undefined,
                adminResponse || undefined
            );

            // Refresh tickets to get updated data
            await fetchTickets();

            alert('Ticket updated successfully!');
            handleCloseViewModal();
        } catch (err) {
            console.error('Failed to update ticket:', err);
            alert(`Failed to update ticket: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };


    // Handle creating a new ticket
    const handleCreateSubmit = async () => {
        // Validate form
        if (!createForm.raisedBy || !createForm.subject || !createForm.description ||
            !createForm.priority ) {
            alert("Please fill in all required fields.");
            return;
        }

        const token = localStorage.getItem("authToken");
        if (!token) {
            setError("Authentication token not found. Please login again.");
            return;
        }

        setCreating(true);
        try {
            // Pass both ticket data and attachment (if any)
            const result = await ticketService.createTicket(
                createForm,           // Ticket data
                createForm.attachment  // Attachment file (or null)
            );


            // Refresh the ticket list
            await fetchTickets();
            alert('Ticket created successfully!');
            handleCloseCreateModal();

        } catch (err) {
            console.error('❌ Failed to create ticket:', err);
            alert(`Failed to create ticket: ${err.message}`);
        } finally {
            setCreating(false);
        }
    };
    // Filter by status using API
    const handleStatusFilterChange = async (e) => {
        const status = e.target.value;
        setSelectedStatusFilter(status);

        if (!hasToken()) return;

        if (status) {
            try {
                const data = await ticketService.getTicketsByStatus(status);
                const transformed = ticketService.transformTickets(data);
                setTickets(transformed);
            } catch (err) {
                console.error(`Failed to fetch tickets with status ${status}:`, err);
                // Fallback to local filtering
                await fetchTickets();
            }
        } else {
            // Fetch all tickets when filter is cleared
            await fetchTickets();
        }
    };

    const showImagePreview = (url) => setImagePreviewModal({ show: true, url });
    const closeImagePreview = () => setImagePreviewModal({ show: false, url: "" });

    const handleOpenCreateModal = () => {
        setShowCreateModal(true);
        setCreateForm({
            raisedBy: "",
            subject: "",
            description: "",
            department: "",
            priority: "",
            issueType: "",
            attachment: null,
        });
        setAttachmentPreview(null);
    };

    const handleCloseCreateModal = () => {
        setShowCreateModal(false);
        setCreateForm({
            raisedBy: "",
            subject: "",
            description: "",
            department: "",
            priority: "",
            issueType: "",
            attachment: null,
        });
        setAttachmentPreview(null);
    };

    const handleCreateFormChange = (field, value) => {
        setCreateForm(prev => ({ ...prev, [field]: value }));
    };

    const handleAttachmentChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCreateForm(prev => ({ ...prev, attachment: file }));
            const reader = new FileReader();
            reader.onloadend = () => setAttachmentPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveAttachment = () => {
        setCreateForm(prev => ({ ...prev, attachment: null }));
        setAttachmentPreview(null);
    };

    return (
        <div className="customer-care-container">
            {/* Stats Dashboard */}
            <div className="stats-dashboard">
                {STATUS_OPTIONS.map((status) => (
                    <div className="stat-card" key={`stat-${status}`}>
                        <div className={`stat-card-inner ${getStatusBadgeClass(status)}`}>
                            <div className="stat-number">{stats[status] || 0}</div>
                            <div className="stat-label">{getStatusLabel(status)}</div>
                        </div>
                    </div>
                ))}
                <div className="stat-card">
                    <div className="stat-card-inner stat-total">
                        <div className="stat-number">{stats.Total || 0}</div>
                        <div className="stat-label">Total Tickets</div>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading && (
                <div className="loading-state" style={{ textAlign: 'center', padding: '20px' }}>
                    <div className="spinner">Loading tickets...</div>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className="error-state" style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: '#dc3545',
                    backgroundColor: '#f8d7da',
                    borderRadius: '8px',
                    margin: '20px 0'
                }}>
                    <span style={{ fontSize: '24px', marginRight: '10px' }}>⚠️</span>
                    {error}
                    <button
                        onClick={fetchTickets}
                        style={{
                            marginLeft: '15px',
                            padding: '8px 20px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Retry
                    </button>
                </div>
            )}

            {/* Filters Section */}
            <div className="filters-section">
                <div className="filters-row">
                    <div className="filter-group">
                        <div className="search-wrapper">
                            <span className="search-icon">🔍</span>
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Search by ID, Raised By, Department, Type, or Description..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <select
                            className="filter-select"
                            value={selectedStatusFilter}
                            onChange={handleStatusFilterChange}
                        >
                            <option value="">All Status</option>
                            {STATUS_OPTIONS.map(s => (
                                <option key={s} value={s}>{getStatusLabel(s)}</option>
                            ))}
                        </select>

                        <select
                            className="filter-select"
                            value={selectedTicketTypeFilter}
                            onChange={(e) => setSelectedTicketTypeFilter(e.target.value)}
                        >
                            <option value="">All Types</option>
                            {TICKET_TYPE_OPTIONS.map(t => (
                                <option key={t} value={t}>{getTicketTypeLabel(t)}</option>
                            ))}
                        </select>

                        <button className="btn-clear-filters" onClick={() => {
                            setSelectedStatusFilter("");
                            setSelectedTicketTypeFilter("");
                            setSearchTerm("");
                            fetchTickets(); // Reset to all tickets
                        }}>
                            Clear All
                        </button>

                        <button
                            className="btn-clear-filters"
                            style={{
                                backgroundColor: 'rgb(230, 109, 51)',
                                color: '#fff',
                                border: '1px solid rgb(230, 109, 51)',
                                fontWeight: 600,
                            }}
                            onClick={handleOpenCreateModal}
                        >
                            + Create Ticket
                        </button>
                    </div>
                </div>
            </div>

            {/* Tickets Table */}
            <div className="table-wrapper">
                <div className="table-header-fixed">
                    <table style={{ tableLayout: 'fixed', width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={{ width: '5%' }}>Ticket</th>
                                <th style={{ width: '9%' }}>Raised By</th>
                                <th style={{ width: '8%' }}>Department</th>
                                <th style={{ width: '8%' }}>Type</th>
                                <th style={{ width: '6%' }}>Priority</th>
                                <th style={{ width: '18%' }}>Message</th>
                                <th style={{ width: '10%' }}>Status</th>
                                <th style={{ width: '8%' }}>Date</th>
                                <th style={{ width: '7%' }}>Time</th>
                                <th style={{ width: '8%' }}>Resolved</th>
                                <th style={{ width: '15%' }}>Response</th>
                                <th style={{ width: '6%' }}>Actions</th>
                            </tr>
                        </thead>
                    </table>
                </div>
                <div className="table-body-scroll">
                    <table style={{ tableLayout: 'fixed', width: '100%' }}>
                        <tbody>
                            {!loading && filteredTickets.length > 0 ? (
                                filteredTickets.map((ticket) => (
                                    <tr key={ticket.ticketId} className="ticket-row">
                                        <td style={{ width: '5%' }}>
                                            <span className="ticket-id">#{ticket.ticketId}</span>
                                        </td>
                                        <td style={{ width: '9%' }}>
                                            <span className="info-value">{ticket.raisedBy}</span>
                                        </td>
                                        <td style={{ width: '8%' }}>
                                            <span className="info-value">{getDepartmentLabel(ticket.department)}</span>
                                        </td>
                                        <td style={{ width: '8%' }}>
                                            <span className={`badge-type ${getTicketTypeBadgeClass(ticket.issueType)}`}>
                                                {getTicketTypeLabel(ticket.issueType)}
                                            </span>
                                        </td>
                                        <td style={{ width: '6%' }}>
                                            <span className={`badge-status ${getPriorityBadgeClass(ticket.priority)}`}>
                                                {getPriorityLabel(ticket.priority)}
                                            </span>
                                        </td>
                                        <td style={{ width: '18%' }} className="message-cell">
                                            <span className="message-text">{ticket.description}</span>
                                        </td>
                                        <td style={{ width: '10%' }}>
                                            <span className={`badge-status ${getStatusBadgeClass(ticket.status)}`}>
                                                {getStatusLabel(ticket.status)}
                                            </span>
                                        </td>
                                        <td style={{ width: '8%' }}>
                                            {formatToISTDate(ticket.createdAt)}
                                        </td>
                                        <td style={{ width: '7%' }}>
                                            {formatToISTTime(ticket.createdAt)}
                                        </td>
                                        <td style={{ width: '8%' }}>
                                            {ticket.resolvedAt ? formatToISTDate(ticket.resolvedAt) : '—'}
                                        </td>
                                        <td style={{ width: '15%' }}>
                                            <span className="" style={{ whiteSpace: "normal", fontSize: "11px" }} title={ticket.adminResponse || "No response"}>
                                                {ticket.adminResponse || "No Response"}
                                            </span>
                                        </td>
                                        <td style={{ width: '6%' }}>
                                            <button
                                                className="btn-view-ticket"
                                                onClick={() => handleShowViewModal(ticket)}
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                !loading && (
                                    <tr>
                                        <td colSpan="12" className="empty-cell">
                                            <div className="empty-state">
                                                <span className="empty-icon">📭</span>
                                                <h5>No internal tickets found</h5>
                                                <p>{searchTerm || selectedStatusFilter || selectedTicketTypeFilter
                                                    ? 'No tickets match your filters.'
                                                    : 'No tickets have been created yet.'}</p>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Image Preview Modal */}
            {imagePreviewModal.show && (
                <div className="image-preview-overlay" onClick={closeImagePreview}>
                    <div className="image-preview-content" onClick={(e) => e.stopPropagation()}>
                        <button className="preview-close" onClick={closeImagePreview}>✕</button>
                        <img src={imagePreviewModal.url} alt="Full size preview" />
                        <div className="preview-actions">
                            <a href={imagePreviewModal.url} target="_blank" rel="noopener noreferrer" className="preview-btn preview-btn-primary">
                                Open in New Tab
                            </a>
                            <a href={imagePreviewModal.url} download className="preview-btn preview-btn-secondary">
                                Download
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* View/Edit Modal */}
            {showViewModal && (
                <div className="view-modal-overlay" onClick={handleCloseViewModal}>
                    <div className="view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="view-modal-header">
                            <h5>
                                <span className="modal-header-icon">🔧</span>
                                {`Internal Ticket #${currentIssueForView?.ticketId || "N/A"}`}
                            </h5>
                            <button className="modal-close-btn" onClick={handleCloseViewModal}>✕</button>
                        </div>

                        <div className="view-modal-body">
                            {currentIssueForView ? (
                                <>
                                    <div className="ticket-info-grid">
                                        <div className="info-item">
                                            <span className="info-label">Ticket ID</span>
                                            <span className="info-value">{currentIssueForView.ticketId}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Type</span>
                                            <span className={`badge-type ${getTicketTypeBadgeClass(currentIssueForView.issueType)}`}>
                                                {getTicketTypeLabel(currentIssueForView.issueType)}
                                            </span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Raised By</span>
                                            <span className="info-value">{currentIssueForView.raisedBy}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Department</span>
                                            <span className="info-value">{getDepartmentLabel(currentIssueForView.department)}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Priority</span>
                                            <span className={`badge-status ${getPriorityBadgeClass(currentIssueForView.priority)}`}>
                                                {getPriorityLabel(currentIssueForView.priority)}
                                            </span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Status</span>
                                            <span className={`badge-status ${getStatusBadgeClass(currentIssueForView.status)}`}>
                                                {getStatusLabel(currentIssueForView.status)}
                                            </span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Created</span>
                                            <span className="info-value">
                                                {formatToISTDateTime(currentIssueForView.createdAt)}
                                            </span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Resolved</span>
                                            <span className="info-value">
                                                {currentIssueForView.resolvedAt ? formatToISTDateTime(currentIssueForView.resolvedAt) : 'Not resolved'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="description-section">
                                        <h6>Issue Description</h6>
                                        <div className="description-content">
                                            {currentIssueForView.description || "No description provided"}
                                        </div>
                                    </div>

                                    <div className="response-section">
                                        <h6>Current Admin Response</h6>
                                        <div className="response-content">
                                            {currentIssueForView.adminResponse ? (
                                                <div className="admin-response-display">
                                                    <span>{currentIssueForView.adminResponse}</span>
                                                </div>
                                            ) : (
                                                <span className="no-response">No response yet</span>
                                            )}
                                        </div>
                                    </div>

                                    <AttachmentViewer
                                        attachmentUrl={currentIssueForView?.attachmentUrl}
                                        showImagePreview={showImagePreview}
                                    />

                                    <div className="update-controls">
                                        <div className="update-group">
                                            <h6>Update Status</h6>
                                            <select
                                                className="update-select"
                                                value={newStatus}
                                                onChange={(e) => setNewStatus(e.target.value)}
                                            >
                                                <option value="">— Select status —</option>
                                                {STATUS_OPTIONS.map(s => (
                                                    <option key={s} value={s}>{getStatusLabel(s)}</option>
                                                ))}
                                            </select>
                                            <div className="current-status">
                                                Current: <span className={`badge-status ${getStatusBadgeClass(currentIssueForView.status)}`}>
                                                    {getStatusLabel(currentIssueForView.status)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="update-group">
                                            <h6>Admin Response</h6>
                                            <textarea
                                                className="update-textarea"
                                                placeholder="Type your admin response here..."
                                                value={adminResponse}
                                                onChange={(e) => setAdminResponse(e.target.value)}
                                                rows="4"
                                            />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="modal-error">
                                    <span className="error-icon">⚠️</span>
                                    <p>Failed to load ticket details</p>
                                </div>
                            )}
                        </div>

                        <div className="view-modal-footer">
                            <button className="btn-modal-close" onClick={handleCloseViewModal}>
                                Close
                            </button>
                            <button
                                className="btn-modal-save"
                                onClick={handleSaveChanges}
                                disabled={(!newStatus && !adminResponse) || saving}
                            >
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Ticket Modal */}
            {showCreateModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1050,
                        backdropFilter: 'blur(4px)',
                    }}
                    onClick={handleCloseCreateModal}
                >
                    <div
                        style={{
                            background: '#ffffff',
                            borderRadius: '20px',
                            width: '100%',
                            maxWidth: '480px',
                            padding: '32px 30px',
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                            position: 'relative',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            scrollbarWidth: "none",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <h5 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#2d3436' }}>Create Ticket</h5>
                            <button
                                onClick={handleCloseCreateModal}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '22px',
                                    cursor: 'pointer',
                                    color: '#636e72',
                                    padding: '4px',
                                    lineHeight: 1,
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <input
                                type="text"
                                placeholder="Raised By *"
                                value={createForm.raisedBy}
                                onChange={(e) => handleCreateFormChange('raisedBy', e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '14px 18px',
                                    border: '1.5px solid #e0e0e0',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    color: '#2d3436',
                                    outline: 'none',
                                    backgroundColor: '#fafafa',
                                    transition: 'border-color 0.2s',
                                    boxSizing: 'border-box',
                                }}
                                onFocus={(e) => e.target.style.borderColor = 'rgb(230, 109, 51)'}
                                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                            />

                            <input
                                type="text"
                                placeholder="Subject *"
                                value={createForm.subject}
                                onChange={(e) => handleCreateFormChange('subject', e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '14px 18px',
                                    border: '1.5px solid #e0e0e0',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    color: '#2d3436',
                                    outline: 'none',
                                    backgroundColor: '#fafafa',
                                    transition: 'border-color 0.2s',
                                    boxSizing: 'border-box',
                                }}
                                onFocus={(e) => e.target.style.borderColor = 'rgb(230, 109, 51)'}
                                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                            />

                            <textarea
                                placeholder="Description *"
                                value={createForm.description}
                                onChange={(e) => handleCreateFormChange('description', e.target.value)}
                                rows="4"
                                style={{
                                    width: '100%',
                                    padding: '14px 18px',
                                    border: '1.5px solid #e0e0e0',
                                    borderRadius: '16px',
                                    fontSize: '14px',
                                    color: '#2d3436',
                                    outline: 'none',
                                    backgroundColor: '#fafafa',
                                    transition: 'border-color 0.2s',
                                    resize: 'vertical',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box',
                                }}
                                onFocus={(e) => e.target.style.borderColor = 'rgb(230, 109, 51)'}
                                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                            />

                            <select
                                value={createForm.department}
                                onChange={(e) => handleCreateFormChange('department', e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '14px 18px',
                                    border: '1.5px solid #e0e0e0',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    color: createForm.department ? '#2d3436' : '#b2bec3',
                                    outline: 'none',
                                    backgroundColor: '#fafafa',
                                    transition: 'border-color 0.2s',
                                    cursor: 'pointer',
                                    appearance: 'auto',
                                    boxSizing: 'border-box',
                                }}
                                onFocus={(e) => e.target.style.borderColor = 'rgb(230, 109, 51)'}
                                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                            >
                                <option value="" disabled>Select Department </option>
                                {DEPARTMENT_OPTIONS.map(dept => (
                                    <option key={dept} value={dept}>{getDepartmentLabel(dept)}</option>
                                ))}
                            </select>

                            <select
                                value={createForm.priority}
                                onChange={(e) => handleCreateFormChange('priority', e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '14px 18px',
                                    border: '1.5px solid #e0e0e0',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    color: createForm.priority ? '#2d3436' : '#b2bec3',
                                    outline: 'none',
                                    backgroundColor: '#fafafa',
                                    transition: 'border-color 0.2s',
                                    cursor: 'pointer',
                                    appearance: 'auto',
                                    boxSizing: 'border-box',
                                }}
                                onFocus={(e) => e.target.style.borderColor = 'rgb(230, 109, 51)'}
                                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                            >
                                <option value="" disabled>Select Priority *</option>
                                {PRIORITY_OPTIONS.map(priority => (
                                    <option key={priority} value={priority}>{getPriorityLabel(priority)}</option>
                                ))}
                            </select>

                            <select
                                value={createForm.issueType}
                                onChange={(e) => handleCreateFormChange('issueType', e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '14px 18px',
                                    border: '1.5px solid #e0e0e0',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    color: createForm.issueType ? '#2d3436' : '#b2bec3',
                                    outline: 'none',
                                    backgroundColor: '#fafafa',
                                    transition: 'border-color 0.2s',
                                    cursor: 'pointer',
                                    appearance: 'auto',
                                    boxSizing: 'border-box',
                                }}
                                onFocus={(e) => e.target.style.borderColor = 'rgb(230, 109, 51)'}
                                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                            >
                                <option value="" disabled>Select Type </option>
                                {TICKET_TYPE_OPTIONS.map(t => (
                                    <option key={t} value={t}>{getTicketTypeLabel(t)}</option>
                                ))}
                            </select>

                            <div
                                style={{
                                    width: '100%',
                                    padding: '12px 18px',
                                    border: '1.5px solid #e0e0e0',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    backgroundColor: '#fafafa',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    boxSizing: 'border-box',
                                }}
                            >
                                {!attachmentPreview ? (
                                    <>
                                        <label
                                            style={{
                                                padding: '4px 14px',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                color: '#555',
                                                backgroundColor: '#fff',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Choose file
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleAttachmentChange}
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                        <span style={{ color: '#b2bec3', fontSize: '13px' }}>No file chosen</span>
                                    </>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                                        <img
                                            src={attachmentPreview}
                                            alt="Preview"
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '6px',
                                                objectFit: 'cover',
                                                border: '1px solid #dee2e6',
                                            }}
                                        />
                                        <span style={{ fontSize: '13px', color: '#2d3436', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {createForm.attachment?.name || 'Image selected'}
                                        </span>
                                        <button
                                            onClick={handleRemoveAttachment}
                                            style={{
                                                background: '#dc3545',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '50%',
                                                width: '22px',
                                                height: '22px',
                                                cursor: 'pointer',
                                                fontSize: '11px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                lineHeight: 1,
                                                flexShrink: 0,
                                            }}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={handleCreateSubmit}
                                disabled={creating}
                                style={{
                                    width: '100%',
                                    padding: '15px',
                                    backgroundColor: creating ? '#ccc' : 'rgb(230, 109, 51)',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '25px',
                                    fontSize: '16px',
                                    fontWeight: 700,
                                    cursor: creating ? 'not-allowed' : 'pointer',
                                    marginTop: '8px',
                                    transition: 'background-color 0.2s, transform 0.1s',
                                    letterSpacing: '0.5px',
                                }}
                                onMouseEnter={(e) => {
                                    if (!creating) e.target.style.backgroundColor = 'rgb(210, 95, 40)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!creating) e.target.style.backgroundColor = 'rgb(230, 109, 51)';
                                }}
                            >
                                {creating ? 'Creating...' : 'Submit Ticket'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InternalTickets;